import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { getWaitlistStatus } from '@/features/onboarding/components/step-tracker/api/waitlist-status/get-waitlist-status';
import { setUserIdentity } from '@/lib/user-identity';
import { handleRolloutDataCheck } from '@/lib/utils/rollout-cookie-check';
import { useBatchSelectionDrawerStore } from '@/stores/batch-selection-drawer';

import { ProfilesResponse } from '../types';

import { getProfiles } from './get-profiles';
import { getReferralData } from './get-referral';
import { getVideoSegmentationData } from './get-video-segmentation-data';
import { selectProfile } from './select-profile';
import { selectVariant } from './select-variant';

// Query Keys
const userKeys = {
  all: ['profiles'] as const,
  list: () => [...userKeys.all, 'list'] as const,
  select: () => [...userKeys.all, 'select'] as const,
  selectVariant: () => [...userKeys.all, 'selectVariant'] as const,
  videoSegmentationData: (sessionId: string) =>
    [...userKeys.all, 'videoSegmentationData', sessionId] as const,
} as const;

const getSelectedProfileFromId = (
  id?: string,
  profiles?: ProfilesResponse.Profile[]
): ProfilesResponse.Profile | undefined => {
  if (!id || !profiles) {
    return undefined;
  }
  return profiles?.find(profile => profile.id === id);
};

const getSelectedCourseFromId = (
  id?: string,
  courses?: ProfilesResponse.EnrolledCourse[]
): ProfilesResponse.EnrolledCourse | undefined => {
  if (!id || !courses) {
    return undefined;
  }
  return courses?.find(course => course.variant_id === id);
};

// Fetch waitlist status for each enrolled course
const useWaitlistStatusQueries = ({
  enrolledCourses,
  isOnboardingCandidate,
}: {
  enrolledCourses: ProfilesResponse.EnrolledCourse[];
  isOnboardingCandidate: boolean;
}) => {
  return useQueries({
    queries:
      enrolledCourses?.map(course => ({
        queryKey: ['waitlistStatus', course.profile_id, course.variant_id],
        queryFn: () => getWaitlistStatus(course.profile_id, course.variant_id),
        enabled:
          isOnboardingCandidate &&
          !!course.sd_onboarding &&
          !course.batch_id &&
          course.status === 'active',
      })) ?? [],
  });
};

// Profiles Query Hook
export const useProfiles = () => {
  return useQuery({
    queryKey: userKeys.list(),
    queryFn: () =>
      getProfiles().then(res => {
        handleRolloutDataCheck(res);
        const selectedProfile = getSelectedProfileFromId(
          res?.selected_profile,
          res?.profiles
        );
        const selectedCourse = getSelectedCourseFromId(
          res?.selected_variant_id,
          selectedProfile?.enrolled_courses
        );

        // Set user identity across all third-party services (Analytics, Sentry, etc.)
        if (res?.selected_profile) {
          setUserIdentity({
            userId: res.selected_profile,
            profile: selectedProfile,
            course: selectedCourse,
          });
        }

        return res;
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

const useProfilesData = () => {
  const { data, isLoading, isFetching, isError, isSuccess } = useProfiles();
  const isDrawerOpen = useBatchSelectionDrawerStore(
    state => state.isDrawerOpen
  );

  const setIsDrawerOpen = useBatchSelectionDrawerStore(
    state => state.setIsDrawerOpen
  );

  const selectedProfile = useMemo(
    () => getSelectedProfileFromId(data?.selected_profile, data?.profiles),
    [data?.selected_profile, data?.profiles]
  );
  const enrolledCourses = useMemo(
    () => selectedProfile?.enrolled_courses,
    [selectedProfile]
  );

  const isOnboardingCandidate = useMemo(
    () => selectedProfile?.onboarding_candidate === true,
    [selectedProfile]
  );

  const waitlistStatusQueries = useWaitlistStatusQueries({
    enrolledCourses: enrolledCourses ?? [],
    isOnboardingCandidate,
  });

  // Merge waitlist status data into enrolled courses
  const enrolledCoursesWithWaitlistStatus = useMemo(() => {
    if (!enrolledCourses) {
      return enrolledCourses;
    }

    return enrolledCourses.map((course, index) => {
      const waitlistStatusData = waitlistStatusQueries?.[index]?.data;
      return {
        ...course,
        waitlist_status:
          waitlistStatusData?.waitlist_entries?.[0]?.variant_id ===
          course.variant_id
            ? waitlistStatusData?.waitlist_entries?.[0]
            : undefined,
        // waitlistStatusData?.waitlist_entries?.[0],
      };
    });
  }, [enrolledCourses, waitlistStatusQueries]);

  const selectedCourse = useMemo(
    () =>
      getSelectedCourseFromId(
        data?.selected_variant_id,
        enrolledCoursesWithWaitlistStatus
      ),
    [enrolledCoursesWithWaitlistStatus, data?.selected_variant_id]
  );

  const onboardingCourses = useMemo(() => {
    return (
      enrolledCoursesWithWaitlistStatus?.filter(
        course => course.sd_onboarding === true
      ) ?? []
    );
  }, [enrolledCoursesWithWaitlistStatus]);

  const oneBatchAssigned = onboardingCourses?.some(course => course.batch_id);
  const oneEnrollmentActive = onboardingCourses?.some(
    course => course.status === 'active'
  );
  const shouldShowOnboarding =
    isOnboardingCandidate && oneEnrollmentActive && !oneBatchAssigned;

  return {
    profiles: data?.profiles ?? [],
    studentProfiles:
      data?.profiles?.filter(profile => profile.role !== 'PARENT') ?? [],
    selectedProfileId: data?.selected_profile,
    selectedVariantId: data?.selected_variant_id,
    // derived data
    selectedProfile,
    enrolledCourses,
    selectedCourse,
    onboardingCourses,
    isOnboardingCandidate,
    parentAppDownloaded: selectedProfile?.first_registered?.some(
      registered => registered.source === 'parent_app'
    ),
    isBundledCourse:
      onboardingCourses?.length && onboardingCourses?.length > 1 ? true : false,
    firstProfileName: selectedProfile?.name?.split(' ')[0] ?? '',
    shouldShowOnboarding,
    // global States
    isDrawerOpen,
    setIsDrawerOpen,
    // states
    isLoading:
      isLoading ||
      isFetching ||
      waitlistStatusQueries.some(query => query.isLoading),
    isFetching,
    isError,
    isSuccess,
    parentEmail:
      selectedProfile?.email ??
      data?.profiles?.find(profile => profile.role === 'PARENT')?.email,
  };
};

const useSelectProfile = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile_id: string) => selectProfile(profile_id),
    mutationKey: userKeys.select(),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

const useSelectVariant = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variant_id: string) => selectVariant(variant_id),
    mutationKey: userKeys.selectVariant(),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

const useGetVideoSegmentationData = (sessionId: string) => {
  const query = useQuery({
    queryKey: userKeys.videoSegmentationData(sessionId),
    queryFn: () => getVideoSegmentationData(sessionId),
    enabled: !!sessionId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  });

  const segments = useMemo(
    () => query.data?.segmentation_data?.segments ?? [],
    [query.data]
  );

  return {
    ...query,
    segments,
  };
};

const useGetReferralData = () => {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: getReferralData,
  });
};

export {
  userKeys,
  useProfilesData,
  useSelectProfile,
  useSelectVariant,
  useGetVideoSegmentationData,
  useGetReferralData,
};
